import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2 } from "lucide-react";
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@wa/ui";
import { ModalShell } from "../_shared/ModalShell";
import { type CrmDuplicateWarning, type Lead, createLead, duplicateRegCode } from "../../../lib/clients";
import { CrossWarning } from "../_shared/CrossWarning";
import { PICKABLE_LEAD_SOURCES } from "../../../lib/leadSource";
import { AriregisterNameInput, type ArPrefill } from "../_shared/AriregisterNameInput";
import type { T } from "./shared";

/** The "add a lead" dialog, with the business register wired into the name. */
export function NewLeadModal({ t, onClose, onCreate }: { t: T; onClose: () => void; onCreate: (lead: Lead) => void }) {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"company" | "person">("company");
  const [email, setEmail] = useState("");
  const [regCode, setRegCode] = useState("");
  const [source, setSource] = useState("käsitsi");
  // Extra fields prefilled from the business register (not shown in the modal,
  // but saved).
  const [vatNumber, setVat] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [website, setWebsite] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The lead this registry code already belongs to, when the save is refused.
  const [duplicate, setDuplicate] = useState<{ id: string; name: string } | null>(null);
  // The opposite case: the save SUCCEEDED, and the company is already a client.
  // The dialog stays open on this, because a lead filed against a paying client
  // is worth one deliberate look before the board scrolls it away.
  const [saved, setSaved] = useState<{ lead: Lead; warnings: CrmDuplicateWarning[] } | null>(null);

  function applyPrefill(p: ArPrefill) {
    setName(p.name);
    setKind("company");
    setSource("ariregister");
    setRegCode(p.regCode);
    setVat(p.vatNumber);
    setEmail(p.email ?? "");
    setPhone(p.phone);
    setWebsite(p.website);
    setAddress(p.address);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setDuplicate(null);
    try {
      const lead = await createLead({
        name: name.trim(), kind, source,
        email: email || undefined,
        regCode: regCode || undefined,
        vatNumber: vatNumber || undefined,
        phone: phone || undefined,
        website: website || undefined,
        address: address || undefined,
      });
      const warnings = lead.warnings ?? [];
      if (warnings.length) {
        // `onCreate` waits for the acknowledgement below rather than firing
        // here: the boards that own this dialog react to it (one of them
        // navigates away), and a warning the operator never sees is the same as
        // no warning at all.
        setSaved({ lead, warnings });
        return;
      }
      onCreate(lead);
      onClose();
    } catch (err) {
      // The registry code is already on the board. Nothing was written, so the
      // form stays as it is and names the record that holds it - the operator
      // either opens that one or edits the code they typed.
      const dup = duplicateRegCode(err);
      if (dup?.leadId) setDuplicate({ id: dup.leadId, name: dup.name });
    } finally { setBusy(false); }
  }

  // Saved, with something worth saying. The form is gone - it has already done
  // its job and a second submit would file the company twice.
  if (saved) {
    const done = () => { onCreate(saved.lead); onClose(); };
    return (
      <ModalShell onClose={done}>
        <div className="w-full max-w-sm rounded-xl border border-border bg-background p-5 shadow-xl">
          <h2 className="mb-3 text-base font-semibold">{t("leads.createdTitle")}</h2>
          <CrossWarning warnings={saved.warnings} />
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" size="sm" onClick={done}>{t("common.close")}</Button>
          </div>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-sm rounded-xl border border-border bg-background p-5 shadow-xl">
        <h2 className="mb-4 text-base font-semibold">{t("leads.addTitle")}</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{t("leads.name")} *</label>
            <AriregisterNameInput
              value={name}
              onChange={setName}
              onPick={applyPrefill}
              placeholder={t("leads.namePlaceholder")}
              required
              enabled={kind === "company"}
              hint={kind === "company" ? t("ariregister.autofill.hint") : undefined}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("leads.type")}</label>
              <Select value={kind} onValueChange={(v) => setKind(v as "company" | "person")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">{t("leads.company")}</SelectItem>
                  <SelectItem value="person">{t("leads.person")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("leads.source")}</label>
              {/* One list, shared with the badge that reads the column back, so
                  a source cannot be offered here and then shown untranslated
                  on the card it was saved to. */}
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PICKABLE_LEAD_SOURCES.map((key) => (
                    <SelectItem key={key} value={key}>{t(`leads.src.${key}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{t("leads.email")}</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("leads.emailPlaceholder")} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{t("leads.regCode")}</label>
            <Input value={regCode} onChange={(e) => setRegCode(e.target.value)} placeholder="12345678" />
          </div>
          {duplicate && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <span>{t("leads.duplicateRegCode", { name: duplicate.name })}</span>
              <Button type="button" variant="outline" size="sm" onClick={() => nav(`/leads/${duplicate.id}`)}>
                {t("leads.openExisting")}
              </Button>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>{t("leads.cancel")}</Button>
            <Button type="submit" size="sm" disabled={busy || !name.trim()}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              {t("leads.add")}
            </Button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
}
